import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaBell,
  FaChevronDown,
  FaExternalLinkAlt,
  FaRegUserCircle,
} from "react-icons/fa";
import {
  getActiveDashboardItem,
  ROLE_LABELS,
} from "./dashboardMenuConfig";

const cn = (...classes) => classes.filter(Boolean).join(" ");

const toneClasses = {
  sky: "bg-sky-50 text-sky-700 border-sky-100",
  violet: "bg-violet-50 text-violet-700 border-violet-100",
  amber: "bg-amber-50 text-amber-700 border-amber-100",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
  rose: "bg-rose-50 text-rose-700 border-rose-100",
};

function formatDate() {
  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(new Date());
}

const DashboardTopbar = ({ role_id, alerts = [], totalAlerts = 0 }) => {
  const location = useLocation();
  const [alertsOpen, setAlertsOpen] = useState(false);

  const activeItem = useMemo(
    () => getActiveDashboardItem(location.pathname, role_id),
    [location.pathname, role_id]
  );

  const pageTitle = activeItem?.title || "Espace personnel";
  const roleLabel = ROLE_LABELS[role_id] || "Utilisateur";

  const activeAlerts = alerts.filter((alert) => Number(alert.count || 0) > 0);
  const displayedAlerts = activeAlerts.length > 0 ? activeAlerts : alerts;

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-[#EEF7FC]/95 px-4 py-3 backdrop-blur-xl sm:px-6 lg:px-8">
      <div className="mx-auto flex w-full max-w-[1680px] items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
            <span>MonAppliClub</span>
            <span className="h-1 w-1 rounded-full bg-slate-300" />
            <span>{roleLabel}</span>
          </div>

          <h1 className="mt-1 truncate text-xl font-bold text-[#123B57] sm:text-2xl">
            {pageTitle}
          </h1>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <div className="hidden rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm lg:block">
            {formatDate()}
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() => setAlertsOpen((prev) => !prev)}
              className="relative flex h-11 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-[#123B57]"
            >
              <span className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-slate-50">
                <FaBell />

                {totalAlerts > 0 && (
                  <span className="absolute -right-1 -top-1 flex min-w-[20px] items-center justify-center rounded-full bg-[#F26B5E] px-1.5 py-0.5 text-[11px] font-bold text-white">
                    {totalAlerts > 99 ? "99+" : totalAlerts}
                  </span>
                )}
              </span>

              <span className="hidden text-sm font-semibold sm:inline">
                Alertes
              </span>

              <FaChevronDown
                className={cn(
                  "hidden text-xs transition-transform sm:block",
                  alertsOpen && "rotate-180"
                )}
              />
            </button>

            {alertsOpen && (
              <div className="absolute right-0 mt-3 w-[340px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.18)]">
                <div className="border-b border-slate-100 px-5 py-4">
                  <div className="text-sm font-bold text-[#123B57]">
                    Centre d’alertes
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    Messages, requêtes et actions à traiter.
                  </div>
                </div>

                <div className="max-h-[380px] overflow-y-auto p-3">
                  {displayedAlerts.length > 0 ? (
                    <div className="space-y-2">
                      {displayedAlerts.map((alert) => {
                        const count = Number(alert.count || 0);
                        const content = (
                          <div
                            className={cn(
                              "flex items-start justify-between gap-3 rounded-2xl border p-3 transition",
                              toneClasses[alert.tone] ||
                                "border-slate-100 bg-slate-50 text-slate-700",
                              alert.path && "hover:brightness-[0.98]"
                            )}
                          >
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="truncate text-sm font-bold">
                                  {alert.label}
                                </span>

                                {alert.pending && (
                                  <span className="rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                                    bientôt
                                  </span>
                                )}
                              </div>

                              <p className="mt-1 line-clamp-2 text-xs opacity-80">
                                {alert.description}
                              </p>
                            </div>

                            <div className="flex shrink-0 items-center gap-2">
                              <span className="rounded-full bg-white px-2.5 py-1 text-xs font-bold shadow-sm">
                                {count}
                              </span>

                              {alert.path && (
                                <FaExternalLinkAlt className="text-[11px] opacity-60" />
                              )}
                            </div>
                          </div>
                        );

                        return alert.path ? (
                          <Link
                            key={alert.key}
                            to={alert.path}
                            onClick={() => setAlertsOpen(false)}
                          >
                            {content}
                          </Link>
                        ) : (
                          <div key={alert.key}>{content}</div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-2xl bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                      Aucune alerte pour le moment.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <Link
            to="/appli/profil"
            className="flex h-11 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-[#123B57]"
          >
            <FaRegUserCircle className="text-lg" />
            <span className="hidden text-sm font-semibold sm:inline">
              Profil
            </span>
          </Link>
        </div>
      </div>
    </header>
  );
};

export default DashboardTopbar;